import { jsx, jsxs } from "react/jsx-runtime";
import * as React from "react";
import { cx } from "../internal/cx.js";
import { useMountedRef, useControllableState } from "./hooks.js";
import { renderShellLink } from "./links.js";
const DEFAULT_LOGIN_LABELS = {
  email: "Email",
  password: "Password",
  submit: "Sign in",
  submitting: "Signing in…",
  forgotPassword: "Forgot password?",
  register: "Create an account",
  missingFields: "Enter your email and password.",
  failed: "Sign-in failed. Try again."
};
const LoginScreen = React.forwardRef(function LoginScreen2({
  onSubmit,
  app,
  brand,
  title = "Sign in",
  subtitle,
  notice,
  error,
  loading = false,
  defaultEmail = "",
  onForgotPassword,
  registerHref,
  renderLink,
  pinMode,
  openInChoice,
  footer,
  getErrorMessage,
  labels: labelsProp,
  className,
  ...rest
}, ref) {
  const labels = { ...DEFAULT_LOGIN_LABELS, ...labelsProp };
  const id = React.useId();
  const emailId = `${id}-email`;
  const passwordId = `${id}-password`;
  const errorId = `${id}-error`;
  const titleId = `${id}-title`;
  const [email, setEmail] = React.useState(defaultEmail);
  const [password, setPassword] = React.useState("");
  const [pending, setPending] = React.useState(false);
  const [formError, setFormError] = React.useState(null);
  const [invalid, setInvalid] = React.useState({ email: false, password: false });
  const emailRef = React.useRef(null);
  const passwordRef = React.useRef(null);
  const mounted = useMountedRef();
  const busy = pending || loading;
  const shownError = formError ?? (error != null && error !== false ? error : null);
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (busy) return;
    const trimmed = email.trim();
    const missing = { email: trimmed === "", password: password === "" };
    if (missing.email || missing.password) {
      setInvalid(missing);
      setFormError(labels.missingFields);
      (missing.email ? emailRef : passwordRef).current?.focus();
      return;
    }
    setInvalid({ email: false, password: false });
    setFormError(null);
    setPending(true);
    try {
      await onSubmit(trimmed, password);
    } catch (err) {
      if (mounted.current) setFormError(messageFor(err, getErrorMessage, labels.failed));
    } finally {
      if (mounted.current) setPending(false);
    }
  };
  const validationError = invalid.email || invalid.password;
  const describeEmail = shownError != null && (!validationError || invalid.email) ? errorId : void 0;
  const describePassword = shownError != null && (!validationError || invalid.password) ? errorId : void 0;
  return /* @__PURE__ */ jsx("div", { ref, className: cx("ev-login", "ev-root", className), "data-app": app, ...rest, children: /* @__PURE__ */ jsxs("section", { className: "ev-login__card", "aria-labelledby": titleId, children: [
    /* @__PURE__ */ jsxs("div", { className: "ev-login__head", children: [
      brand != null && /* @__PURE__ */ jsx("div", { className: "ev-login__brand", children: brand }),
      /* @__PURE__ */ jsx("h1", { className: "ev-title ev-login__title", id: titleId, children: title }),
      subtitle != null && /* @__PURE__ */ jsx("p", { className: "ev-muted ev-login__subtitle", children: subtitle })
    ] }),
    notice != null && /* @__PURE__ */ jsx("div", { className: "ev-banner ev-login__notice", children: notice }),
    pinMode != null ? /* @__PURE__ */ jsx("div", { className: "ev-login__pin", children: pinMode }) : (
      // method="post": a submit before hydration, or after hydration failed, never puts the password in the URL.
      /* @__PURE__ */ jsxs(
        "form",
        {
          className: "ev-login__form",
          method: "post",
          noValidate: true,
          onSubmit: handleSubmit,
          "aria-busy": busy || void 0,
          children: [
            /* @__PURE__ */ jsxs("div", { className: "ev-field", children: [
              /* @__PURE__ */ jsx("label", { htmlFor: emailId, children: labels.email }),
              /* @__PURE__ */ jsx(
                "input",
                {
                  ref: emailRef,
                  id: emailId,
                  className: "ev-input",
                  type: "email",
                  name: "email",
                  autoComplete: "username",
                  autoCapitalize: "none",
                  spellCheck: false,
                  placeholder: labels.emailPlaceholder || void 0,
                  required: true,
                  value: email,
                  onChange: (e) => {
                    setEmail(e.target.value);
                    if (invalid.email) setInvalid((prev) => ({ ...prev, email: false }));
                  },
                  "aria-invalid": invalid.email || void 0,
                  "aria-describedby": describeEmail
                }
              )
            ] }),
            /* @__PURE__ */ jsxs("div", { className: "ev-field", children: [
              /* @__PURE__ */ jsx("label", { htmlFor: passwordId, children: labels.password }),
              /* @__PURE__ */ jsx(
                "input",
                {
                  ref: passwordRef,
                  id: passwordId,
                  className: "ev-input",
                  type: "password",
                  name: "password",
                  autoComplete: "current-password",
                  placeholder: labels.passwordPlaceholder || void 0,
                  required: true,
                  value: password,
                  onChange: (e) => {
                    setPassword(e.target.value);
                    if (invalid.password) setInvalid((prev) => ({ ...prev, password: false }));
                  },
                  "aria-invalid": invalid.password || void 0,
                  "aria-describedby": describePassword
                }
              )
            ] }),
            openInChoice && /* @__PURE__ */ jsx(LoginChoiceGroup, { choice: openInChoice, fallbackName: `${id}-choice` }),
            shownError != null && /* @__PURE__ */ jsx("div", { className: "ev-banner ev-login__error", "data-tone": "danger", role: "alert", id: errorId, children: shownError }),
            /* @__PURE__ */ jsx(
              "button",
              {
                type: "submit",
                className: "ev-btn ev-btn--primary ev-btn--block",
                "aria-disabled": busy || void 0,
                "data-busy": busy ? "true" : void 0,
                children: busy ? labels.submitting : labels.submit
              }
            )
          ]
        }
      )
    ),
    (onForgotPassword && pinMode == null || registerHref != null) && /* @__PURE__ */ jsxs("div", { className: "ev-login__links", children: [
      onForgotPassword && pinMode == null && /* @__PURE__ */ jsx("button", { type: "button", className: "ev-login__link", onClick: () => onForgotPassword(email.trim()), children: labels.forgotPassword }),
      registerHref != null && renderShellLink(
        renderLink,
        { href: registerHref, className: "ev-login__link", children: labels.register },
        { id: "register", href: registerHref }
      )
    ] }),
    footer != null && /* @__PURE__ */ jsx("div", { className: "ev-login__foot", children: footer })
  ] }) });
});
function messageFor(err, getErrorMessage, fallback) {
  if (!getErrorMessage) return fallback;
  const custom = getErrorMessage(err);
  return custom == null || custom === "" ? fallback : custom;
}
function LoginChoiceGroup({ choice, fallbackName }) {
  const first = choice.options[0]?.value ?? "";
  const [value, setValue] = useControllableState(choice.value, choice.defaultValue ?? first, choice.onValueChange);
  const name = choice.name ?? fallbackName;
  return /* @__PURE__ */ jsxs("fieldset", { className: "ev-login__choice", children: [
    /* @__PURE__ */ jsx("legend", { className: "ev-field__label", children: choice.legend ?? "Open in" }),
    /* @__PURE__ */ jsx("div", { className: "ev-login__options", children: choice.options.map((option) => /* @__PURE__ */ jsxs("label", { className: "ev-login__option", children: [
      /* @__PURE__ */ jsx(
        "input",
        {
          type: "radio",
          className: "ev-check",
          name,
          value: option.value,
          checked: value === option.value,
          onChange: () => setValue(option.value)
        }
      ),
      /* @__PURE__ */ jsx("span", { children: option.label })
    ] }, option.value)) })
  ] });
}
export {
  DEFAULT_LOGIN_LABELS,
  LoginScreen
};
//# sourceMappingURL=LoginScreen.js.map
